import maplibregl from 'maplibre-gl';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { MapLibreProvider, useMapLibre } from '../../MapLibre/MapLibreContext';
import OverzoomTileLayer from '../../MapLibre/OverzoomTileLayer';
import type { SmartAttributionMapProps } from './types';

const DEFAULT_VIEW = {
  center: [46.603354, 1.888334],
  zoom: 5,
};

const COPRO_MARKER_SIZE = 14;
const MANAGER_MARKER_SIZE = 24;
const HIGHLIGHT_SCALE = 1.35;
const DEFAULT_COPRO_COLOR = '#6b7280';

export const MARKER_COLORS = [
  '#2563eb', '#dc2626', '#16a34a', '#d97706', '#9333ea', '#0891b2', '#ea580c', '#0ea5e9', '#f43f5e', '#22c55e',
];

interface MarkerReference {
  marker: maplibregl.Marker;
  inner: HTMLDivElement;
  defaultColor: string;
  ownerId?: number;
}

const buildMarkerElement = ({
  color,
  size,
  label,
  hasBorder,
  title,
  zIndex,
}: {
  color: string;
  size: number;
  label?: string;
  hasBorder?: boolean;
  title?: string;
  zIndex?: number;
}) => {
  const wrapper = document.createElement('div');
  wrapper.style.display = 'flex';
  wrapper.style.alignItems = 'center';
  wrapper.style.justifyContent = 'center';
  wrapper.style.pointerEvents = 'auto';
  if (zIndex !== undefined) {
    wrapper.style.zIndex = zIndex.toString();
  }

  const inner = document.createElement('div');
  inner.style.width = `${size}px`;
  inner.style.height = `${size}px`;
  inner.style.borderRadius = '9999px';
  inner.style.backgroundColor = color;
  inner.style.color = '#fff';
  inner.style.display = 'flex';
  inner.style.alignItems = 'center';
  inner.style.justifyContent = 'center';
  inner.style.fontSize = '10px';
  inner.style.fontWeight = '600';
  inner.style.boxShadow = '0 4px 10px rgba(0, 0, 0, 0.25)';
  inner.style.opacity = '0.8';
  inner.style.transition = 'transform 160ms ease, opacity 160ms ease, box-shadow 160ms ease';
  inner.style.transform = 'scale(1)';
  if (hasBorder) {
    inner.style.border = '2px solid #ffffff';
  }

  if (label) {
    inner.textContent = label;
  }
  if (title) {
    inner.title = title;
  }

  inner.style.pointerEvents = 'none';

  wrapper.appendChild(inner);
  return { wrapper, inner };
};

const applyHighlightStyles = (
  refs: Map<number, MarkerReference>,
  highlightedUserId: number | null | undefined,
) => {
  refs.forEach(({ inner, defaultColor, ownerId }) => {
    inner.style.backgroundColor = defaultColor;
    inner.style.transform = 'scale(1)';
    inner.style.opacity = ownerId ? '0.8' : '0.65';
    inner.style.boxShadow = '0 4px 10px rgba(0, 0, 0, 0.25)';

    if (highlightedUserId && ownerId === highlightedUserId) {
      inner.style.transform = `scale(${HIGHLIGHT_SCALE})`;
      inner.style.opacity = '1';
      inner.style.boxShadow = '0 6px 14px rgba(0, 0, 0, 0.35)';
    }
  });
};

type SmartAttributionMarkersProps = SmartAttributionMapProps & {
  onUserInteraction?: () => void;
};

/**
 * Renders smart attribution markers and tracks user interactions to pause auto-fit.
 */
const SmartAttributionMarkers: React.FC<SmartAttributionMarkersProps> = ({
  coproMarkers,
  managerMarkers,
  highlightedUserId,
  onHighlight,
  onManagerMove,
  readOnly,
  onUserInteraction,
}) => {
  const map = useMapLibre();
  const coproRefs = useRef<Map<number, MarkerReference>>(new Map());
  const managerRefs = useRef<Map<number, MarkerReference>>(new Map());

  // Cleanup all markers on unmount
  useEffect(() => () => {
    coproRefs.current.forEach(({ marker }) => marker.remove());
    coproRefs.current.clear();
    managerRefs.current.forEach(({ marker }) => marker.remove());
    managerRefs.current.clear();
  }, []);

  const managerColorLookup = useMemo(() => {
    const lookup = new Map<number, string>();
    managerMarkers.forEach((marker) => {
      lookup.set(marker.userId, marker.color);
    });
    return lookup;
  }, [managerMarkers]);

  useEffect(() => {
    managerRefs.current.forEach(({ marker }) => marker.remove());
    managerRefs.current.clear();

    managerMarkers.forEach((markerData) => {
      const color = markerData.color || MARKER_COLORS[markerData.userId % MARKER_COLORS.length];
      const { wrapper, inner } = buildMarkerElement({
        color,
        size: MANAGER_MARKER_SIZE,
        hasBorder: true,
        label: markerData.userName?.charAt(0)?.toUpperCase() ?? undefined,
        title: markerData.userName,
        zIndex: 1000, // Ensure manager markers are on top of copro markers
      });

      wrapper.classList.add('rgpd-draggable-marker');

      wrapper.addEventListener('pointerdown', () => onUserInteraction?.());
      wrapper.addEventListener('click', () => onUserInteraction?.());
      wrapper.addEventListener('mouseenter', () => onHighlight?.(markerData.userId));
      wrapper.addEventListener('mouseleave', () => onHighlight?.(null));
      wrapper.style.cursor = markerData.draggable && !readOnly ? 'grab' : 'pointer';

      const marker = new maplibregl.Marker({
        element: wrapper,
        draggable: Boolean(markerData.draggable) && !readOnly,
      })
        .setLngLat([markerData.long, markerData.lat])
        .addTo(map);

      if (marker.isDraggable() && onManagerMove) {
        marker.on('dragstart', () => {
          onUserInteraction?.();
          wrapper.style.cursor = 'grabbing';
        });
        marker.on('dragend', () => {
          const { lat, lng } = marker.getLngLat();
          onManagerMove?.(markerData.userId, { lat, long: lng });
          onUserInteraction?.();
          wrapper.style.cursor = 'grab';
        });
      }

      managerRefs.current.set(markerData.userId, {
        marker,
        inner,
        defaultColor: color,
        ownerId: markerData.userId,
      });
    });

    applyHighlightStyles(managerRefs.current, highlightedUserId);
  }, [map, managerMarkers, onHighlight, onManagerMove, readOnly, onUserInteraction]);

  useEffect(() => {
    coproRefs.current.forEach(({ marker }) => marker.remove());
    coproRefs.current.clear();

    coproMarkers.forEach((markerData) => {
      const color = markerData.assignedUserId != null
        ? managerColorLookup.get(markerData.assignedUserId) ?? DEFAULT_COPRO_COLOR
        : DEFAULT_COPRO_COLOR;

      const { wrapper, inner } = buildMarkerElement({
        color,
        size: COPRO_MARKER_SIZE,
        hasBorder: false,
        title: markerData.coproName,
        zIndex: 500, // Ensure copro markers are below manager markers
      });

      wrapper.classList.add('rgpd-draggable-marker');

      wrapper.addEventListener('pointerdown', () => onUserInteraction?.());
      wrapper.addEventListener('click', () => onUserInteraction?.());
      wrapper.addEventListener('mouseenter', () => onHighlight?.(markerData.assignedUserId ?? null));
      wrapper.addEventListener('mouseleave', () => onHighlight?.(null));

      const marker = new maplibregl.Marker({ element: wrapper })
        .setLngLat([markerData.long, markerData.lat])
        .setPopup(new maplibregl.Popup({ closeButton: false, offset: 12 }).setText(
          `${markerData.coproName} • ${Math.round(markerData.salesAmount)} €`,
        ))
        .addTo(map);

      coproRefs.current.set(markerData.coproId, {
        marker,
        inner,
        defaultColor: color,
        ownerId: markerData.assignedUserId ?? undefined,
      });
    });

    applyHighlightStyles(coproRefs.current, highlightedUserId);
  }, [map, coproMarkers, managerColorLookup, onHighlight, onUserInteraction]);

  useEffect(() => {
    applyHighlightStyles(managerRefs.current, highlightedUserId);
    applyHighlightStyles(coproRefs.current, highlightedUserId);
  }, [highlightedUserId]);

  return null;
};

/**
 * Controls map viewport to fit copro and manager markers smoothly unless the user interacts.
 */
const BoundsController: React.FC<{
  coproMarkers: SmartAttributionMapProps['coproMarkers'];
  managerMarkers: SmartAttributionMapProps['managerMarkers'];
  userInteractedRef: React.MutableRefObject<boolean>;
}> = ({ coproMarkers, managerMarkers, userInteractedRef }) => {
  const map = useMapLibre();
  const lastBoundsKeyRef = useRef<string | null>(null);
  const pendingFitRef = useRef<number | null>(null);
  const markersSignatureRef = useRef<string>('');

  const computeBounds = useCallback((): maplibregl.LngLatBounds | null => {
    const points: Array<[number, number]> = [];

    coproMarkers.forEach((copro) => {
      points.push([copro.long, copro.lat]);
    });

    managerMarkers.forEach((manager) => {
      points.push([manager.long, manager.lat]);
    });

    if (points.length === 0) {
      return null;
    }

    const bounds = points.reduce((acc, [lng, lat]) => acc.extend([lng, lat]), new maplibregl.LngLatBounds(points[0], points[0]));
    return bounds;
  }, [coproMarkers, managerMarkers]);

  const buildSignature = useCallback(() => {
    const serialize = ({ lat, long }: { lat: number; long: number }) => `${lat.toFixed(5)}:${long.toFixed(5)}`;
    const coproSignature = coproMarkers.map((marker) => `${marker.coproId}:${serialize(marker)}`).sort().join('|');
    const managerSignature = managerMarkers.map((marker) => `${marker.userId}:${serialize(marker)}`).sort().join('|');
    return `${coproMarkers.length};${managerMarkers.length};${coproSignature}#${managerSignature}`;
  }, [coproMarkers, managerMarkers]);

  const scheduleFit = useCallback((bounds: maplibregl.LngLatBounds, signature: string) => {
    if (userInteractedRef.current) {
      return;
    }

    const boundsKey = bounds.toArray().flat().map((value) => value.toFixed(6)).join(',');
    if (boundsKey === lastBoundsKeyRef.current) {
      return;
    }
    lastBoundsKeyRef.current = boundsKey;

    if (pendingFitRef.current !== null) {
      window.clearTimeout(pendingFitRef.current);
    }

    pendingFitRef.current = window.setTimeout(() => {

      if (userInteractedRef.current) {
        return;
      }

      try {
        map.fitBounds(bounds, {
        padding: 72,
          duration: 800,
          maxZoom: 16.5,
          easing: (t) => 1 - ((1 - t) * (1 - t)),
        });
        markersSignatureRef.current = signature;
      } catch (error) {
        console.error('BoundsController: fitBounds error', error);
      }
    }, 100);
  }, [map, userInteractedRef]);

  useEffect(() => {
    if (userInteractedRef.current) {
      return;
    }

    const signature = buildSignature();
    if (signature === markersSignatureRef.current) {
      return;
    }

    const bounds = computeBounds();
    if (!bounds && markersSignatureRef.current) {
      markersSignatureRef.current = signature;
    }
    if (!bounds) {
      return;
    }

    const triggerFit = () => scheduleFit(bounds, signature);

    if (map.loaded() && map.isStyleLoaded()) {
      triggerFit();
      return;
    }

    const handleReady = () => {

      // Try to wait a bit for the map to be fully ready
      const checkMapReady = () => {
        if (map.loaded() && map.isStyleLoaded()) {
          map.off('load', handleReady);
          map.off('styledata', handleReady);
          triggerFit();
        } else {
          setTimeout(checkMapReady, 50);
        }
      };

      checkMapReady();
    };

    // Add logging for all relevant map events


    map.on('load', () => {
      handleReady();
    });
    map.on('styledata', () => {
      handleReady();
    });

    // Fallback: if map doesn't become ready within 5 seconds, try anyway
    const fallbackTimeout = setTimeout(() => {
      if (map.loaded() && map.isStyleLoaded()) {
        triggerFit();
      }
    }, 5000);

    return () => {
      map.off('load', handleReady);
      map.off('styledata', handleReady);
      clearTimeout(fallbackTimeout);
    };
  }, [buildSignature, computeBounds, map, scheduleFit, userInteractedRef]);

  useEffect(() => () => {
    if (pendingFitRef.current !== null) {
      window.clearTimeout(pendingFitRef.current);
    }
  }, []);

  return null;
};

const AttributionMap: React.FC<SmartAttributionMapProps> = ({
  coproMarkers,
  managerMarkers,
  highlightedUserId,
  onHighlight,
  onManagerMove,
  readOnly,
}) => {
  const userInteractedRef = useRef(false);
  const markUserInteraction = useCallback(() => {
    userInteractedRef.current = true;
  }, []);

  return (
    <MapLibreProvider
      center={DEFAULT_VIEW.center as [number, number]}
      zoom={DEFAULT_VIEW.zoom}
      allowPitch={false}
      className="h-[360px] w-full overflow-hidden rounded-lg border border-gray-200"
      jwt={null}
    >
      <OverzoomTileLayer isCadastre={false} />
      <SmartAttributionMarkers
        coproMarkers={coproMarkers}
        managerMarkers={managerMarkers}
        highlightedUserId={highlightedUserId}
        onHighlight={onHighlight}
        onManagerMove={onManagerMove}
        readOnly={readOnly}
        onUserInteraction={markUserInteraction}
      />
      <BoundsController
        coproMarkers={coproMarkers}
        managerMarkers={managerMarkers}
        userInteractedRef={userInteractedRef}
      />
    </MapLibreProvider>
  );
};

export default AttributionMap;

